import { useState, useEffect, useRef } from "react";
import { api, getErrorMessage } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Database, Download, Mail, Loader2, CheckCircle, Clock, Upload, FileArchive, AlertTriangle } from "lucide-react";

const formatDateTime = (iso) => {
  if (!iso) return "-";
  return new Date(iso).toLocaleString("fr-FR", {
    weekday: "long", day: "numeric", month: "long",
    year: "numeric", hour: "2-digit", minute: "2-digit"
  });
};

const formatSize = (bytes) => {
  if (!bytes) return "0 Ko";
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / 1024 / 1024).toFixed(2)} Mo`;
};

export const BackupSection = () => {
  const [settings, setSettings] = useState({ auto_email_backup: false, email: "" });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [sending, setSending] = useState(false);

  // Restore state
  const fileInputRef = useRef(null);
  const [restoreFile, setRestoreFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [restoreMode, setRestoreMode] = useState("merge");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);

  const refresh = async () => {
    try {
      const [s, st] = await Promise.all([api.getBackupSettings(), api.getBackupStatus()]);
      setSettings({
        auto_email_backup: s.data.auto_email_backup || false,
        email: s.data.email || "",
      });
      setStatus(st.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const handleSave = async () => {
    if (settings.auto_email_backup && !settings.email) {
      toast.error("Veuillez saisir un email pour la sauvegarde automatique");
      return;
    }
    try {
      setSaving(true);
      await api.updateBackupSettings(settings);
      toast.success("Paramètres de sauvegarde enregistrés");
      await refresh();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur de sauvegarde"));
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const res = await api.downloadBackup();
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const disposition = res.headers?.["content-disposition"] || "";
      const match = /filename="?([^";]+)"?/i.exec(disposition);
      link.download = match ? match[1] : `chantierflow-backup-${Date.now()}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Sauvegarde téléchargée");
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur lors du téléchargement"));
    } finally {
      setDownloading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!settings.email) {
      toast.error("Veuillez saisir un email de destination");
      return;
    }
    try {
      setSending(true);
      const res = await api.sendBackupEmail(settings.email);
      toast.success(res.data.message || "Sauvegarde envoyée par email");
      await refresh();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur d'envoi"));
    } finally {
      setSending(false);
    }
  };

  const handleFileChange = async (e) => {
    const f = e.target.files?.[0];
    setRestoreResult(null);
    setPreview(null);
    if (!f) { setRestoreFile(null); return; }
    setRestoreFile(f);
    try {
      setPreviewing(true);
      const res = await api.previewRestore(f);
      setPreview(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err, "Archive invalide"));
      setRestoreFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } finally {
      setPreviewing(false);
    }
  };

  const handleConfirmRestore = async () => {
    if (!restoreFile) return;
    try {
      setRestoring(true);
      const res = await api.restoreBackup(restoreFile, restoreMode);
      setRestoreResult(res.data);
      toast.success(res.data.message);
      await refresh();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur de restauration"));
    } finally {
      setRestoring(false);
      setConfirmOpen(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
    <Card data-testid="backup-section">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <Database className="h-5 w-5 text-primary" />
          Sauvegarde de la base de données
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-sm text-muted-foreground">
          Téléchargez ou recevez par email une archive ZIP contenant l'ensemble de vos données (JSON par collection).
          La sauvegarde automatique s'exécute chaque jour à 03h00 (Europe/Paris).
        </p>

        <div className="space-y-2">
          <Label htmlFor="backup-email">Email de destination</Label>
          <Input
            id="backup-email"
            type="email"
            value={settings.email}
            onChange={(e) => setSettings({ ...settings, email: e.target.value })}
            placeholder="votre@email.com"
            data-testid="backup-email-input"
          />
        </div>

        <div className="flex items-center justify-between p-3 rounded-sm border">
          <div>
            <p className="font-medium text-sm">Sauvegarde automatique quotidienne par email</p>
            <p className="text-xs text-muted-foreground">Envoi chaque jour à 03h00 de l'archive ZIP à l'email ci-dessus</p>
          </div>
          <Switch
            checked={settings.auto_email_backup}
            onCheckedChange={(v) => setSettings({ ...settings, auto_email_backup: v })}
            data-testid="toggle-auto-backup"
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button onClick={handleSave} disabled={saving} data-testid="save-backup-settings-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
            Enregistrer
          </Button>
          <Button variant="outline" onClick={handleDownload} disabled={downloading} data-testid="download-backup-btn">
            {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            Télécharger maintenant
          </Button>
          <Button variant="outline" onClick={handleSendEmail} disabled={sending} data-testid="send-backup-email-btn">
            {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
            Envoyer par email
          </Button>
        </div>

        {status && (
          <div className="p-3 rounded-sm border space-y-2 bg-muted/30">
            <p className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4 text-primary" />
              Statut
            </p>
            <div className="grid sm:grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-muted-foreground">Prochain envoi automatique : </span>
                <span className="font-medium">{formatDateTime(status.next_run)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Dernière sauvegarde : </span>
                <span className="font-medium">{formatDateTime(status.last_backup_at)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Taille : </span>
                <span className="font-medium">{formatSize(status.last_backup_size_bytes)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Documents : </span>
                <span className="font-medium">{status.last_backup_documents || 0}</span>
              </div>
              {status.last_backup_email && (
                <div className="sm:col-span-2">
                  <span className="text-muted-foreground">Envoyée à : </span>
                  <span className="font-medium">{status.last_backup_email}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>

    <Card data-testid="backup-restore-section" className="mt-6">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <Upload className="h-5 w-5 text-primary" />
          Restauration depuis une sauvegarde
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-start gap-2 p-3 rounded-sm border border-amber-300 bg-amber-50 text-amber-900">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <p className="text-xs">
            Sélectionnez une archive ZIP Bati Assistant pour restaurer vos données. Les documents seront forcément
            rattachés à <strong>votre compte</strong> (isolation multi-tenant stricte).
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="restore-file">Archive ZIP de sauvegarde</Label>
          <Input
            id="restore-file"
            ref={fileInputRef}
            type="file"
            accept=".zip,application/zip"
            onChange={handleFileChange}
            data-testid="restore-file-input"
          />
        </div>

        {previewing && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Analyse de l'archive...
          </div>
        )}

        {preview && (
          <div className="p-3 rounded-sm border bg-muted/30 space-y-2" data-testid="restore-preview">
            <p className="text-sm font-medium flex items-center gap-2">
              <FileArchive className="h-4 w-4 text-primary" />
              Aperçu de l'archive
            </p>
            <div className="grid sm:grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-muted-foreground">Compte d'origine : </span>
                <span className="font-medium">{preview.manifest?.user?.email || "-"}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Export du : </span>
                <span className="font-medium">{formatDateTime(preview.manifest?.exported_at)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Documents : </span>
                <span className="font-medium">{preview.total_documents}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Collections : </span>
                <span className="font-medium">{Object.keys(preview.collections || {}).length}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Taille : </span>
                <span className="font-medium">{formatSize(preview.archive_size_bytes)}</span>
              </div>
            </div>
            {preview.manifest?.user?.email && preview.manifest.user.id !== undefined && (
              <p className="text-xs text-amber-700">
                Note : les données seront réattribuées à votre compte actuel.
              </p>
            )}
          </div>
        )}

        {restoreFile && preview && (
          <>
            <div className="space-y-2">
              <Label>Mode de restauration</Label>
              <div className="flex flex-col sm:flex-row gap-2">
                <label className={`flex-1 flex items-start gap-2 p-3 rounded-sm border cursor-pointer ${restoreMode === "merge" ? "border-primary bg-primary/5" : ""}`}>
                  <input
                    type="radio"
                    name="restore-mode"
                    value="merge"
                    checked={restoreMode === "merge"}
                    onChange={() => setRestoreMode("merge")}
                    className="mt-1"
                    data-testid="restore-mode-merge"
                  />
                  <span className="text-sm">
                    <span className="font-medium block">Fusion (recommandé)</span>
                    <span className="text-xs text-muted-foreground">
                      Ajoute/met à jour les documents par ID. Conserve vos données actuelles non présentes dans l'archive.
                    </span>
                  </span>
                </label>
                <label className={`flex-1 flex items-start gap-2 p-3 rounded-sm border cursor-pointer ${restoreMode === "replace" ? "border-destructive bg-destructive/5" : ""}`}>
                  <input
                    type="radio"
                    name="restore-mode"
                    value="replace"
                    checked={restoreMode === "replace"}
                    onChange={() => setRestoreMode("replace")}
                    className="mt-1"
                    data-testid="restore-mode-replace"
                  />
                  <span className="text-sm">
                    <span className="font-medium block text-destructive">Remplacement complet</span>
                    <span className="text-xs text-muted-foreground">
                      Supprime toutes vos données actuelles des collections présentes dans l'archive, puis insère celles de la sauvegarde.
                    </span>
                  </span>
                </label>
              </div>
            </div>

            <Button
              onClick={() => setConfirmOpen(true)}
              disabled={restoring}
              variant={restoreMode === "replace" ? "destructive" : "default"}
              data-testid="restore-start-btn"
            >
              {restoring ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              Lancer la restauration
            </Button>
          </>
        )}

        {restoreResult && (
          <div className="p-3 rounded-sm border bg-green-50 border-green-300 text-green-900 space-y-2" data-testid="restore-result">
            <p className="text-sm font-medium flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              Restauration terminée ({restoreResult.mode})
            </p>
            <div className="grid sm:grid-cols-3 gap-2 text-xs">
              <div>Insérées : <strong>{restoreResult.total_inserted}</strong></div>
              <div>Mises à jour : <strong>{restoreResult.total_updated}</strong></div>
              <div>Supprimées : <strong>{restoreResult.total_deleted}</strong></div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>

    <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Confirmer la restauration</AlertDialogTitle>
          <AlertDialogDescription>
            {restoreMode === "replace" ? (
              <>
                Cette opération va <strong className="text-destructive">supprimer définitivement</strong> vos données actuelles
                des {preview ? Object.keys(preview.collections || {}).length : 0} collection(s) concernée(s), puis restaurer
                les {preview?.total_documents || 0} document(s) de l'archive. Cette action est irréversible.
              </>
            ) : (
              <>
                Cette opération va fusionner {preview?.total_documents || 0} document(s) avec vos données actuelles
                (ajout ou mise à jour par ID).
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="restore-confirm-cancel">Annuler</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirmRestore}
            className={restoreMode === "replace" ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}
            data-testid="restore-confirm-ok"
          >
            {restoring ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Confirmer la restauration
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
};

export default BackupSection;
